import { Dialog } from "@headlessui/react";
import { MenuAlt2Icon, XIcon } from "@heroicons/react/outline";
import * as Sentry from "@sentry/react";
import { AnimatePresence, motion } from "framer-motion";
import size from "lodash/size";
import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import SidebarSyncingError from "@apps/main/components/sidebar-syncing-error";
import MeetingsPanel from "@apps/meetings/meetings-panel";
import SearchModal from "@apps/search/search";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  currentOrganizationVar,
  currentUserVar,
  errorNotificationVar,
} from "@cache/cache";
import BillingDialogCTA from "@components/billing-dialog-cta/billing-dialog-cta";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import { useLink } from "@components/link/link-helpers";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { getNodeUrl, isCtrlSpaceEvent } from "@helpers/helpers";

import Logo from "./logo";
import { LayoutNotification } from "./notification";
import OutdatedEditorLayout from "./outdated-editor-dialog";
import LayoutAddSearch from "./sidebar-add-search";
import SidebarHijack from "./sidebar-hijack";
import SidebarManagersReports from "./sidebar-managers-reports";
import SidebarNavigation from "./sidebar-navigation";
import SidebarNavigationOld from "./sidebar-navigation-old";
import SidebarOnboarding from "./sidebar-onboarding";
import SidebarSettings from "./sidebar-settings";
import SidebarSyncingCalendar from "./sidebar-syncing-calendar";
import TrackPreviousUrl from "./track-previous-url";

const Layout: React.FC<PropsWithChildren> = ({ children }) => {
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const location = useLocation<TFLocationState>();
  const link = useLink();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isShowingSearchModal, setIsShowingSearchModal] = useState(false);
  const menuIsCollapsed = !uiPreferenceCache.leftNavigationSidebarExpanded;

  const handleToggleMenu = () => {
    saveUiPreference({
      leftNavigationSidebarExpanded:
        !uiPreferenceCache.leftNavigationSidebarExpanded,
    });
  };

  // Show messages coming from the back-end
  useEffect(() => {
    if (size(window.errorMessages) > 0) {
      errorNotificationVar({
        title: "Error",
        description: window.errorMessages[0],
        timeout: null,
      });
      window.errorMessages = [];
    }
  }, []);

  const handleShowSearchModal = useCallback(() => {
    if (!location.state?.background) {
      setIsShowingSearchModal(true);
    }
  }, [location]);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (isCtrlSpaceEvent(event)) {
        handleShowSearchModal();
      }
    },
    [handleShowSearchModal],
  );

  const handleHideSearchModal = useCallback(() => {
    setIsShowingSearchModal(false);
  }, []);

  const handleClickSearchModalResult = useCallback(
    (result: Parameters<typeof getNodeUrl>[0]) => {
      handleHideSearchModal();
      link.redirect(getNodeUrl(result) || "/");
    },
    [handleHideSearchModal, link],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <>
      <div className="min-h-full flex flex-1">
        {/* Prompt to refresh dialog when editor version changes. See https://github.com/Topicflow/topicflow/pull/721 */}
        <OutdatedEditorLayout />
        <TrackPreviousUrl />
        {isShowingSearchModal && (
          <SearchModal
            onClose={handleHideSearchModal}
            onClickResult={handleClickSearchModalResult}
          />
        )}

        {/* Mobile nav */}
        <Dialog
          as="div"
          open={sidebarOpen}
          className="fixed inset-0 flex z-leftSidebar md:hidden tf-print-display-none"
          onClose={setSidebarOpen}
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />

          <div className="relative flex-1 flex flex-col max-w-xs w-full pt-3 bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <span className="sr-only">Close sidebar</span>
                <XIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex-shrink-0 flex justify-between items-center px-4">
              <Logo />
              <LayoutAddSearch
                menuIsCollapsed={menuIsCollapsed}
                onShowSearchModal={handleShowSearchModal}
              />
            </div>
            <div className="mt-3 flex-1 h-0 flex flex-col border-t">
              <nav className="space-y-1 flex-1 overflow-y-auto py-2">
                <div className="px-2 pb-2">
                  {waffle.flag_is_active("new-left-nav") ? (
                    <>
                      <SidebarNavigation
                        onClickSearch={handleShowSearchModal}
                        menuIsCollapsed={false}
                      />
                      <SidebarManagersReports
                        menuIsCollapsed={menuIsCollapsed}
                      />
                    </>
                  ) : (
                    <SidebarNavigationOld
                      onClickSearch={handleShowSearchModal}
                      menuIsCollapsed={false}
                    />
                  )}
                </div>
                {currentOrganization.featureFlags.meetings && (
                  <div className="px-2 border-t flex-1 flex flex-col overflow-hidden">
                    <MeetingsPanel />
                  </div>
                )}
              </nav>

              <div className="flex items-center justify-center text-sm bg-white border-r border-t">
                <SidebarSettings />
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 w-14"></div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div
          className={classNames(
            "hidden md:flex md:flex-col md:fixed md:inset-y-0 z-10 bg-sidenav-back tf-print-display-none",
            menuIsCollapsed ? "md:w-16" : "md:w-52 lg:w-60",
          )}
          id="sidebar"
        >
          {!menuIsCollapsed && (
            <div
              className={classNames(
                "flex h-14 border-r items-center flex-shrink-0 pl-5 pr-3 gap-2 justify-between",
                waffle.flag_is_active("new-left-nav") ? "" : "border-b",
              )}
            >
              <Logo menuIsCollapsed={menuIsCollapsed} />
              <LayoutAddSearch
                menuIsCollapsed={menuIsCollapsed}
                onShowSearchModal={handleShowSearchModal}
              />
            </div>
          )}
          <div
            className={classNames(
              "border-r flex-1 flex flex-col text-sm",
              menuIsCollapsed ? "bg-white" : "bg-sidenav-back",
              !menuIsCollapsed &&
                uiPreferenceCache.leftNavigationMeetingsFullExpanded
                ? "overflow-hidden"
                : "overflow-y-auto",
            )}
          >
            {/* Nav items - hidden when meetings is full expanded */}
            <AnimatePresence initial={false}>
              {!(
                !menuIsCollapsed &&
                uiPreferenceCache.leftNavigationMeetingsFullExpanded
              ) && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="overflow-hidden shrink-0"
                >
                  <div
                    className={classNames(
                      "py-2",
                      !menuIsCollapsed && "px-3",
                      menuIsCollapsed && "w-[36px] mx-auto",
                    )}
                  >
                    {waffle.flag_is_active("new-left-nav") ? (
                      <>
                        <SidebarNavigation
                          menuIsCollapsed={menuIsCollapsed}
                          onClickSearch={handleShowSearchModal}
                        />
                        <SidebarManagersReports
                          menuIsCollapsed={menuIsCollapsed}
                        />
                      </>
                    ) : (
                      <SidebarNavigationOld
                        menuIsCollapsed={menuIsCollapsed}
                        onClickSearch={handleShowSearchModal}
                      />
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Meetings section */}
            {!menuIsCollapsed && currentOrganization.featureFlags.meetings && (
              <>
                <div className="shrink-0 px-3 pt-5">
                  {uiPreferenceCache.leftNavigationMeetingsFullExpanded ? (
                    <Button
                      theme={buttonTheme.text}
                      size={ButtonSize.small}
                      onClick={() =>
                        saveUiPreference({
                          leftNavigationMeetingsFullExpanded: false,
                        })
                      }
                    >
                      <TopicflowIcon type="arrowLeft" className="h-3 w-3" />
                      Back to Navigation
                    </Button>
                  ) : (
                    <div className="flex items-center justify-between">
                      <button
                        className="text-gray-500 text-xs flex items-center gap-0.5 rounded px-2 py-1 hover:bg-nav-item-active"
                        onClick={() =>
                          saveUiPreference({
                            leftNavigationMeetingsExpanded:
                              !uiPreferenceCache.leftNavigationMeetingsExpanded,
                            leftNavigationMeetingsFullExpanded: false,
                          })
                        }
                      >
                        <span>Today's Meetings</span>
                        <TopicflowIcon
                          type="chevronDown"
                          className={classNames(
                            "h-3 w-3 text-gray-500 transition-transform duration-200 ease-in-out",
                            uiPreferenceCache.leftNavigationMeetingsExpanded
                              ? "rotate-180"
                              : "",
                          )}
                        />
                      </button>
                      {uiPreferenceCache.leftNavigationMeetingsExpanded && (
                        <Button
                          icon={getTopicflowIcon("expandArrows")}
                          theme={buttonTheme.iconGray}
                          size={ButtonSize.medium}
                          onClick={() =>
                            saveUiPreference({
                              leftNavigationMeetingsFullExpanded: true,
                            })
                          }
                        />
                      )}
                    </div>
                  )}
                </div>
                {uiPreferenceCache.leftNavigationMeetingsExpanded && (
                  <Sentry.ErrorBoundary
                    fallback={<div>Cannot load meetings.</div>}
                    showDialog
                  >
                    <MeetingsPanel
                      compact={
                        !uiPreferenceCache.leftNavigationMeetingsFullExpanded
                      }
                    />
                  </Sentry.ErrorBoundary>
                )}
              </>
            )}
            {/* Onboarding - hidden when meetings is full expanded */}
            {!menuIsCollapsed &&
              currentOrganization.featureFlags.meetings &&
              !uiPreferenceCache.leftNavigationMeetingsFullExpanded && (
                <div className="pt-4 px-2 shrink-0">
                  <SidebarOnboarding currentUser={currentUser} />
                  <SidebarSyncingCalendar />
                  <SidebarSyncingError />
                </div>
              )}
          </div>
          <SidebarHijack menuIsCollapsed={menuIsCollapsed} />
          <div className="text-sm border-r">
            <SidebarSettings
              menuIsCollapsed={menuIsCollapsed}
              onToggleMenu={handleToggleMenu}
            />
          </div>
        </div>

        <div
          className={classNames(
            "min-h-full flex flex-col flex-1 min-w-0",
            menuIsCollapsed ? "md:pl-16" : "md:pl-52 lg:pl-60",
            "print:pl-0",
          )}
        >
          {window.readOnlyMode && (
            <div className="p-3 bg-orange-300">
              Topicflow is currently in read-only mode. You may view content but
              not edit it.
            </div>
          )}

          {currentUser.hasOrgWithTooManyUsers && (
            <div className="px-6 py-2 min-h-14 flex gap-2 justify-between items-center bg-yellow-50 border-b align-middle text-sm antialiased">
              Your organization on the free tier currently has more than 10
              users.
              <BillingDialogCTA className="text-blue-link hover:underline">
                Contact us to upgrade.
              </BillingDialogCTA>
            </div>
          )}

          <div
            className="flex md:hidden sticky top-0 z-leftSidebar flex-shrink-0 h-14 bg-white border-b tf-print-display-none"
            aria-label="top-navbar"
          >
            <div className="flex-1 px-4 flex items-center">
              <Logo />
            </div>
            <button
              type="button"
              className="px-4 border-l border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
              onClick={() => setSidebarOpen(true)}
              id="js-mobile-navbar-toggle-button"
            >
              <span className="sr-only">Open sidebar</span>
              <MenuAlt2Icon className="h-6 w-6" />
            </button>
          </div>
          <main
            className={classNames(
              "flex-1 flex flex-col relative",
              "bg-gray-50",
            )}
          >
            {children}
          </main>
        </div>
      </div>
      <LayoutNotification />
    </>
  );
};

export default Layout;
